<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions vuejs-accessibility/mouse-events-have-key-events -->
  <Component
    :is="component.is"
    v-bind="component.attrs"
    class="panorama-scroller"
    @mousedown="onMouseDown"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="onClick"
    @dragstart.prevent="onDragStart"
    @touchstart="onTouchStart"
  >
    <div class="panorama-scroller-image" :style="style">
      <!-- eslint-disable-next-line vuejs-accessibility/alt-text -->
      <img ref="imageEl" :src="image" />
      <!-- eslint-disable-next-line vuejs-accessibility/alt-text -->
      <img :src="image" />
    </div>
  </Component>
</template>

<script lang="ts" setup>
import { modulo } from '~/helpers'

const props = withDefaults(
  defineProps<{
    image?: string
    link?: string
  }>(),
  {
    image: '',
    link: '',
  },
)

const x = ref(0)
const accelerationCurrent = ref(1)
const accelerationTarget = ref(1)
const raf = ref<number>()
const autoplay = ref(true)
const mouseIsDown = ref(false)
const startX = ref(0)
const mouseStartX = ref(0)
const isMoving = ref(false)

const imageEl = ref<HTMLImageElement>()

const style = computed(() => ({
  transform: `translateX(-${x.value}px)`,
}))

const component = computed(() => {
  if (props.link) {
    return {
      is: 'a',
      attrs: {
        href: props.link,
        target: '_blank',
      },
    }
  }
  return {
    is: 'div',
    attrs: {},
  }
})

function onTouchStart(e: TouchEvent) {
  onMouseDown({ clientX: e.touches[0]?.clientX }, true)
}

function onClick(e: MouseEvent) {
  if (isMoving.value) {
    e.preventDefault()
  }
}

function onMouseEnter() {
  accelerationTarget.value = 0
}

function onMouseLeave() {
  accelerationTarget.value = 1
}

function onMouseMove(e: MouseEvent | TouchEvent) {
  const currentX = 'touches' in e ? e.touches[0]?.clientX : e.clientX
  if (mouseIsDown.value && currentX) {
    const mouseXDiff = currentX - mouseStartX.value
    const width = imageEl.value?.width || 0
    x.value = modulo(startX.value - mouseXDiff, width)
    if (!isMoving.value && Math.abs(currentX - mouseStartX.value) > 5) {
      isMoving.value = true
    }
  }
}

function onMouseDown(e: Partial<MouseEvent>, isTouch = false) {
  autoplay.value = false
  mouseIsDown.value = true
  isMoving.value = false
  startX.value = x.value
  mouseStartX.value = e.clientX!

  if (isTouch) {
    window.addEventListener('touchend', onMouseUp)
    window.addEventListener('touchmove', onMouseMove)
  } else {
    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('mousemove', onMouseMove)
  }
}

function onMouseUp() {
  mouseIsDown.value = false
  autoplay.value = true
  window.removeEventListener('mouseup', onMouseUp)
  window.removeEventListener('mousemove', onMouseMove)
}

function onDragStart() {
  return false
}

function loop() {
  const width = imageEl.value?.width || 0
  if (width && autoplay.value) {
    x.value = (x.value + accelerationCurrent.value) % width
  }

  if (Math.abs(accelerationTarget.value - accelerationCurrent.value) >= 0.05) {
    if (accelerationCurrent.value < accelerationTarget.value) {
      accelerationCurrent.value += 0.05
    } else {
      accelerationCurrent.value -= 0.05
    }
  } else {
    accelerationCurrent.value = accelerationTarget.value
  }

  raf.value = window.requestAnimationFrame(loop)
}

onBeforeUnmount(() => {
  if (raf.value) {
    window.cancelAnimationFrame(raf.value)
  }
})

onMounted(loop)
</script>

<style>
@reference '~/assets/css/tailwind.css';
.panorama-scroller {
  @apply relative block overflow-hidden;
  height: 225px;
  @variant tablet {
    height: 450px;
  }
}

.panorama-scroller-image {
  @apply pointer-events-none absolute top-0 left-0 h-full;

  img {
    @apply relative block h-full w-auto;
    max-width: none;
    &:nth-child(2) {
      @apply absolute top-0 left-0;
      transform: translateX(100%);
    }
  }
}
</style>
